import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssistantPairingResponse, AssistantStatusResponse } from '@familyhub/shared';
import { forget, getAssistant, putAssistantSettings, requestPairingCode, unpair } from './api';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { useSession } from '../session';

/**
 * `/huib` — the household's Signal assistant (docs/ASSISTENT.md §11).
 *
 * Four things, top to bottom: whether he is set up and connected (so "he doesn't
 * answer" has somewhere to be diagnosed), pairing, the switches, and his memory.
 * Online-only like the recipe settings; a module-level cache keeps the last
 * answer across navigation.
 */

let cached: AssistantStatusResponse | null = null;

const dateFmt = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' });
const timeFmt = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });

function linkLine(s: AssistantStatusResponse): { text: string; problem: boolean } {
  if (!s.configured) return { text: `${s.name} is nog niet ingesteld op de server.`, problem: true };
  switch (s.link.state) {
    case 'connected':
      return s.model_configured
        ? { text: `${s.name} is verbonden met Signal.`, problem: false }
        : { text: `${s.name} is verbonden en herinnert, maar zijn model is niet ingesteld — praten lukt nog niet.`, problem: true };
    case 'never':
      return { text: `${s.name} is ingesteld maar heeft Signal nog nooit bereikt.`, problem: true };
    default:
      return {
        text: `${s.name} heeft sinds ${s.link.heartbeat_at ? timeFmt.format(new Date(s.link.heartbeat_at)) : '—'} niets van Signal gehoord.${
          s.link.last_error ? ` (${s.link.last_error})` : ''
        }`,
        problem: true,
      };
  }
}

export function HuibScreen() {
  const session = useSession();
  const me = session.status === 'in' ? session.me?.user.id : undefined;
  const [data, setData] = useState<AssistantStatusResponse | null>(cached);
  const [failed, setFailed] = useState(false);
  const [code, setCode] = useState<AssistantPairingResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (res: AssistantStatusResponse) => {
    cached = res;
    setData(res);
    setFailed(false);
  };

  const refresh = useCallback(async () => {
    try {
      apply(await getAssistant());
    } catch {
      if (!cached) setFailed(true);
    }
  }, []);
  useRefreshOnReturn(refresh);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(fn: () => Promise<AssistantStatusResponse>) {
    if (busy) return;
    setBusy(true);
    try {
      apply(await fn());
    } catch {
      // A failed change leaves the screen as it was; the next refresh is the truth.
    } finally {
      setBusy(false);
    }
  }

  async function pair() {
    setBusy(true);
    try {
      setCode(await requestPairingCode());
    } catch {
      setCode(null);
    } finally {
      setBusy(false);
    }
  }

  const name = data?.name ?? 'Huib';
  const mine = data?.members.find((m) => m.user_id === me);

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het overzicht">
          ‹ Hub
        </Link>
        <h1>{name}</h1>
        <span style={{ width: 52 }} aria-hidden />
      </div>

      <main className="stack">
        {data === null ? (
          <p className="row-sub">{failed ? `${name} is nu niet bereikbaar — probeer het zo nog eens.` : 'Laden…'}</p>
        ) : (
          <>
            <p className={linkLine(data).problem ? 'banner' : 'row-sub wraps'} role="status">
              {linkLine(data).text}
            </p>

            <p className="section-label">Koppelen</p>
            <div className="card">
              {data.members.map((m) => (
                <div className="row" key={m.user_id}>
                  <span className="row-main">
                    <span className="row-name">{m.display_name}</span>
                    <span className="row-sub">
                      {m.paired && m.paired_at ? `Gekoppeld sinds ${dateFmt.format(new Date(m.paired_at))}` : 'Niet gekoppeld'}
                    </span>
                  </span>
                  {m.paired && (
                    <button
                      className="btn btn-quiet"
                      disabled={busy}
                      aria-label={`Ontkoppel ${m.display_name}`}
                      onClick={() => void run(() => unpair(m.user_id))}
                    >
                      Ontkoppel
                    </button>
                  )}
                </div>
              ))}
            </div>
            {code ? (
              <div className="card huib-code">
                <p className="row-sub wraps">Stuur dit in de Signal-groep{data.group_bound ? '' : ' waar ' + name + ' in moet wonen'}:</p>
                <p className="huib-code-text" aria-label={`Koppelbericht: @${name} koppel ${code.code}`}>
                  @{name} koppel {code.code}
                </p>
                <p className="row-sub">Geldig tot {timeFmt.format(new Date(code.expires_at))}.</p>
              </div>
            ) : (
              <button className="btn btn-primary" disabled={busy || !data.configured} onClick={() => void pair()}>
                {mine?.paired ? 'Opnieuw koppelen' : 'Koppel Signal'}
              </button>
            )}

            <p className="section-label">Instellingen</p>
            <div className="card">
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Leest altijd mee</span>
                  <span className="row-sub wraps">
                    Aan: {name} reageert op alles in de groep — en alles wat jullie daar zeggen gaat naar zijn
                    model (Scaleway, EU). Uit: alleen als je hem roept.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.always_respond}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ always_respond: e.target.checked }))}
                />
              </label>
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Ochtendbericht</span>
                  <span className="row-sub">Agenda, taken en regen voor vandaag.</span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.briefing_enabled}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ briefing_enabled: e.target.checked }))}
                />
              </label>
              {data.settings.briefing_enabled && (
                <label className="row">
                  <span className="row-main">
                    <span className="row-name">Om</span>
                  </span>
                  <input
                    type="time"
                    aria-label="Tijd van het ochtendbericht"
                    value={data.settings.briefing_time}
                    disabled={busy}
                    onChange={(e) => {
                      if (/^\d\d:\d\d$/.test(e.target.value)) void run(() => putAssistantSettings({ briefing_time: e.target.value }));
                    }}
                  />
                </label>
              )}
            </div>

            <p className="section-label">Geheugen</p>
            {data.memories.length >= data.memory_cap && (
              <p className="banner">Het geheugen is vol ({data.memory_cap}). Gooi iets weg, anders onthoudt {name} niets nieuws.</p>
            )}
            {data.memories.length === 0 ? (
              <p className="row-sub wraps">
                Nog niets. Zeg “{name}, onthoud dat…” in de groep. Wachtwoorden, pincodes en BSN onthoudt hij nooit.
              </p>
            ) : (
              <div className="card">
                {data.memories.map((m) => (
                  <div className="row row-top" key={m.id}>
                    <span className="row-main">
                      <span className="row-name wraps">{m.text}</span>
                      <span className="row-sub">
                        {m.source === 'told' ? 'Verteld' : 'Opgepikt'}
                        {m.created_by_name ? ` door ${m.created_by_name}` : ''} · {dateFmt.format(new Date(m.created_at))}
                      </span>
                    </span>
                    <button
                      className="btn btn-quiet"
                      disabled={busy}
                      aria-label={`Vergeet: ${m.text}`}
                      onClick={() => void run(() => forget(m.id))}
                    >
                      Vergeet
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
